@if (!drawerMode) {
  <app-page-breadcrumb [pageTitle]="pageTitle"></app-page-breadcrumb>
}

<ng-container
  *ngIf="drawerMode; then formBody; else pageWrapper"
></ng-container>

<ng-template #pageWrapper>
  <div class="grid grid-cols-1 gap-6">
    <app-component-card
      [title]="pageTitle"
      desc="Complete los datos del paquete"
    >
      <ng-container *ngTemplateOutlet="formBody"></ng-container>
    </app-component-card>
  </div>
</ng-template>

<ng-template #formBody>
  <div [class.p-6]="drawerMode">
    <!-- Loading -->
    @if (loading) {
      <div class="flex justify-center items-center py-12">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"
        ></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400"
          >Cargando datos...</span
        >
      </div>
    }

    <!-- Error -->
    @if (error) {
      <div
        class="mb-4 p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20"
      >
        <div class="flex items-center gap-3">
          <svg
            class="w-5 h-5 text-error-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            ></path>
          </svg>
          <p class="text-error-600 dark:text-error-400">{{ error }}</p>
        </div>
      </div>
    }

    @if (!loading) {
      <form
        [formGroup]="form"
        (ngSubmit)="guardar()"
        class="space-y-5"
      >
        <!-- Nombre y SKU -->
        <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label
              class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Nombre del Paquete <span class="text-error-500">*</span>
            </label>
            <input
              type="text"
              formControlName="name"
              placeholder="Pack Empresa Estándar"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-500]="hasError('name')"
              [class.border-gray-300]="!hasError('name')"
            />
            @if (hasError('name')) {
              <p class="mt-1.5 text-sm text-error-500">
                {{ getError('name') }}
              </p>
            }
          </div>
          <div>
            <label
              class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              SKU
            </label>
            <input
              type="text"
              formControlName="sku"
              placeholder="Se autogenera (PKG-####) si lo dejas vacío"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              [class.border-error-500]="hasError('sku')"
              [class.border-gray-300]="!hasError('sku')"
            />
            @if (hasError('sku')) {
              <p class="mt-1.5 text-sm text-error-500">{{ getError('sku') }}</p>
            }
            <p class="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Si lo dejas vacío, se generará automáticamente (PKG-####).
            </p>
          </div>
        </div>

        <!-- Descripción -->
        <div>
          <label
            class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >Descripción</label
          >
          <textarea
            formControlName="description"
            rows="3"
            placeholder="Paquete de productos y servicios..."
            class="dark:bg-dark-900 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800 resize-y"
            [class.border-error-500]="
              hasError('description') || descriptionLength > 500
            "
            [class.border-gray-300]="
              !hasError('description') && descriptionLength <= 500
            "
          ></textarea>

          <!-- Mensaje de error + contador en la misma fila -->
          <div class="mt-1.5 flex items-start justify-between gap-3">
            <div class="flex-1">
              @if (hasError('description')) {
                <p class="text-sm text-error-500">
                  {{ getError('description') }}
                </p>
              } @else if (descriptionLength > 500) {
                <p class="text-sm text-error-500">
                  La descripción no puede superar los 500 caracteres.
                </p>
              }
            </div>
            <p
              class="text-xs tabular-nums whitespace-nowrap"
              [class.text-gray-500]="descriptionLength <= 450"
              [class.dark:text-gray-400]="descriptionLength <= 450"
              [class.text-warning-500]="
                descriptionLength > 450 && descriptionLength <= 500
              "
              [class.text-error-500]="descriptionLength > 500"
            >
              {{ descriptionLength }} / 500
            </p>
          </div>
        </div>

        <!-- Precios y moneda -->
        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <h4
            class="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-4"
          >
            Precios
          </h4>
          <div class="grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div>
              <label
                class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Moneda</label
              >
              <select
                formControlName="currencyId"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
              >
                <option [ngValue]="null">Sin especificar</option>
                @for (moneda of monedas; track moneda.id) {
                  <option [ngValue]="moneda.id">
                    {{ moneda.name }} ({{ moneda.code }} {{ moneda.prefix }})
                  </option>
                }
              </select>
            </div>
            <div>
              <label
                class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Estrategia de precio</label
              >
              <select
                formControlName="priceStrategy"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
              >
                <option value="fixed">Precio fijo</option>
                <option value="sum_of_components">Suma de componentes</option>
              </select>
            </div>
            <div>
              @if (!isSumOfComponents) {
                <label
                  class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >Precio del Paquete</label
                >
                <input
                  type="number"
                  formControlName="bundlePrice"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
                />
                @if (hasError('bundlePrice')) {
                  <p class="mt-1.5 text-sm text-error-500">
                    {{ getError('bundlePrice') }}
                  </p>
                }
              } @else {
                <label
                  class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >Precio calculado</label
                >
                <div
                  class="h-11 flex items-center px-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm text-gray-500 dark:text-gray-400"
                >
                  Se calcula automáticamente
                </div>
                @if (precioCalculadoLocal > 0) {
                  <p
                    class="mt-1 text-xs text-brand-600 dark:text-brand-400 font-medium"
                  >
                    Calculado: {{ precioCalculadoLocal | number: '1.2-2' }}
                  </p>
                } @else if (currentBundle?.calculatedPrice !== undefined) {
                  <p
                    class="mt-1 text-xs text-brand-600 dark:text-brand-400 font-medium"
                  >
                    Calculado:
                    {{ currentBundle?.calculatedPrice | number: '1.2-2' }}
                  </p>
                } @else {
                  <p class="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    Agrega items para ver el precio calculado
                  </p>
                }
              }
            </div>
          </div>

          <!-- Descuento, Modo de visualización y Rol -->
          <div class="grid grid-cols-1 gap-5 sm:grid-cols-3 mt-5">
            <div>
              <label
                class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Descuento (%)</label
              >
              <input
                type="number"
                formControlName="discountPercent"
                step="0.01"
                min="0"
                max="100"
                placeholder="0"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              />
              @if (hasError('discountPercent')) {
                <p class="mt-1.5 text-sm text-error-500">
                  {{ getError('discountPercent') }}
                </p>
              }
            </div>
            <div>
              <label
                class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Modo de visualización</label
              >
              <select
                formControlName="displayMode"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
              >
                <option value="single_line">Línea única</option>
                <option value="breakdown">Desglose</option>
              </select>
            </div>
            <div>
              <label
                class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Rol del paquete</label
              >
              <select
                formControlName="bundleRole"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
              >
                <option value="standard">Estándar</option>
                <option value="order_bump">Order Bump</option>
                <option value="upsell">Upsell</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Vigencia promocional (Incidente #33) -->
        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div class="mb-2 flex items-center justify-between">
            <h4 class="text-sm font-semibold text-gray-800 dark:text-white">
              Vigencia promocional
            </h4>
            <div class="flex items-center gap-2">
              <!-- Incidente #56: estado autoritativo del backend (promoStatus) -->
              @if (isEditing && currentBundle) {
                <span
                  class="text-xs font-medium px-2 py-0.5 rounded-full"
                  [ngClass]="promoStatusClass"
                  >{{ promoStatusLabel }}</span
                >
              }
              @if (tieneVigenciaPromo || promoPriceValue !== null) {
                <button
                  type="button"
                  (click)="removePromotion()"
                  class="text-xs font-medium text-error-500 hover:underline"
                >
                  Quitar promoción
                </button>
              }
            </div>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-3">
            La promoción se activa y desactiva automáticamente según estas
            fechas.
          </p>

          <!-- Precio promocional + interruptor (Incidente #56) -->
          <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 mb-4">
            <div>
              <label
                class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Precio promocional
                @if (tieneVigenciaPromo) {
                  <span class="text-error-500">*</span>
                }</label
              >
              <input
                type="number"
                step="0.01"
                min="0"
                formControlName="promoPrice"
                placeholder="Sin promoción"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                [class.border-error-500]="
                  promoPriceInvalid || promoPriceRequiredMissing
                "
                [class.border-gray-300]="
                  !promoPriceInvalid && !promoPriceRequiredMissing
                "
              />
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Debe ser menor que el precio del paquete. Obligatorio si defines
                una vigencia.
              </p>
            </div>
            <div class="flex items-start gap-3 sm:pt-8">
              <input
                type="checkbox"
                formControlName="promoEnabled"
                id="bundlePromoEnabled"
                class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-800"
              />
              <div>
                <label
                  for="bundlePromoEnabled"
                  class="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >Promoción activa</label
                >
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  Desactívala para suspender la promoción sin borrar sus fechas.
                </p>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label
                class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Inicio de vigencia</label
              >
              <input
                type="date"
                [min]="minDatetimeLocal"
                formControlName="promoValidFrom"
                class="dark:[&::-webkit-calendar-picker-indicator]:invert dark:[&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:appearance-auto! [&::-webkit-calendar-picker-indicator]:block! dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              />
            </div>
            <div>
              <label
                class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >Fin de vigencia</label
              >
              <input
                type="date"
                [min]="minDatetimeLocal"
                formControlName="promoValidUntil"
                class="dark:[&::-webkit-calendar-picker-indicator]:invert dark:[&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:appearance-auto! [&::-webkit-calendar-picker-indicator]:block! dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              />
            </div>
          </div>
          @if (promoRangeInvalid) {
            <p class="mt-2 text-xs text-error-500">
              La fecha de fin de vigencia no puede ser anterior a la fecha de
              inicio.
            </p>
          }
          @if (promoPriceInvalid) {
            <p class="mt-2 text-xs text-error-500">
              El precio promocional debe ser mayor que 0 y menor que el precio
              del paquete.
            </p>
          }
          @if (promoDatesRequiredMissing) {
            <p class="mt-2 text-xs text-error-500">
              Para definir un precio promocional debes indicar la fecha de
              inicio y la de fin de vigencia.
            </p>
          }
          @if (promoPriceRequiredMissing) {
            <p class="mt-2 text-xs text-error-500">
              Para definir una vigencia promocional debes indicar el precio
              promocional; si no, la promoción no se aplicaría.
            </p>
          }
        </div>

        <!-- Imagen y Estado -->
        <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label
                class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >URL de Imagen</label
              >
              <input
                type="text"
                formControlName="imageUrl"
                placeholder="https://cdn.ejemplo.com/bundle.png"
                class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-brand-800"
              />
            </div>
            <div class="flex items-start gap-3 pt-2 sm:pt-7">
              <input
                type="checkbox"
                formControlName="isActive"
                id="bundleIsActive"
                class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-800"
              />
              <div>
                <label
                  for="bundleIsActive"
                  class="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >Paquete activo</label
                >
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  Si está desmarcado, el paquete no estará disponible
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Bundle Items (solo en modo edición, cuando el bundle ya existe) -->
        @if ((isEditing && bundleId) || !isEditing) {
          <div class="pt-4 border-t border-gray-200 dark:border-gray-700">
            <div class="flex items-center justify-between mb-4">
              <h4
                class="text-sm font-semibold text-gray-600 dark:text-gray-400"
              >
                Items del Paquete
                @if (!isEditing) {
                  <span class="text-error-500">*</span>
                }
                @if (!isEditing && bundleItems.length > 0) {
                  <span
                    class="ml-2 text-xs font-normal text-brand-600 dark:text-brand-400"
                  >
                    (se guardarán al crear el paquete)
                  </span>
                }
              </h4>
              @if (!showItemForm) {
                <button
                  type="button"
                  (click)="abrirFormItem()"
                  class="px-2.5 py-1 text-xs rounded-md font-medium bg-brand-500 text-white hover:bg-brand-600 flex items-center gap-1"
                >
                  <svg
                    class="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 4v16m8-8H4"
                    ></path>
                  </svg>
                  Añadir Item
                </button>
              }
            </div>

            <!-- Item inline form -->
            @if (showItemForm) {
              <div
                class="border border-brand-200 dark:border-brand-800/40 rounded-lg p-3 bg-brand-50/40 dark:bg-brand-900/10 space-y-3 mb-3"
              >
                <div class="flex items-center justify-between">
                  <span
                    class="text-xs font-semibold text-brand-600 dark:text-brand-400"
                  >
                    {{ editingItemId !== null ? 'Editar item' : 'Nuevo item' }}
                  </span>
                  <button
                    type="button"
                    (click)="cerrarFormItem()"
                    class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      ></path>
                    </svg>
                  </button>
                </div>

                <div
                  [formGroup]="itemForm"
                  class="space-y-2"
                >
                  <!-- Product search -->
                  <div>
                    <label
                      class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5"
                      >Producto <span class="text-error-500">*</span></label
                    >
                    <div class="relative">
                      <input
                        type="text"
                        [(ngModel)]="productSearch"
                        [ngModelOptions]="{ standalone: true }"
                        (ngModelChange)="buscarProductos()"
                        (focus)="onFocusProducto()"
                        (blur)="onBlurProducto()"
                        placeholder="Buscar producto..."
                        class="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      />
                      <!-- Dropdown results -->
                      @if (productResults.length > 0) {
                        <div
                          class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-40 overflow-y-auto"
                        >
                          @for (prod of productResults; track prod.id) {
                            <button
                              type="button"
                              (click)="seleccionarProducto(prod)"
                              class="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 dark:hover:bg-brand-900/20"
                            >
                              <span
                                class="font-medium text-gray-800 dark:text-white"
                                >{{ prod.name }}</span
                              >
                              <span class="text-gray-400 ml-2"
                                >({{ prod.sku }})</span
                              >
                              <span
                                class="text-brand-600 ml-auto float-right"
                                >{{
                                  formatPrecio(prod.price, prod.currencyId)
                                }}</span
                              >
                            </button>
                          }
                        </div>
                      }
                      @if (searchingProducts) {
                        <div
                          class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-2"
                        >
                          <span class="text-xs text-gray-500 dark:text-gray-400"
                            >Buscando...</span
                          >
                        </div>
                      }
                    </div>
                    @if (selectedProduct) {
                      <div class="mt-1 flex items-center gap-2">
                        <span
                          class="text-xs text-brand-600 dark:text-brand-400 font-medium"
                          >Seleccionado: {{ selectedProduct.name }}</span
                        >
                        <button
                          type="button"
                          (click)="
                            selectedProduct = null;
                            productSearch = '';
                            itemForm.patchValue({ idProduct: null })
                          "
                          class="text-gray-400 hover:text-gray-600"
                        >
                          <svg
                            class="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M6 18L18 6M6 6l12 12"
                            ></path>
                          </svg>
                        </button>
                      </div>
                    }
                  </div>

                  <!-- Quantity, Optional -->
                  <div class="grid grid-cols-2 gap-2">
                    <div>
                      <label
                        class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-0.5"
                        >Cantidad</label
                      >
                      <input
                        type="number"
                        formControlName="quantity"
                        step="0.01"
                        min="0.01"
                        class="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      />
                    </div>
                    <div class="flex items-end pb-1.5">
                      <label class="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          formControlName="isOptional"
                          class="h-3.5 w-3.5 rounded border-gray-300 text-brand-500 dark:border-gray-600 dark:bg-gray-800"
                        />
                        <span class="text-xs text-gray-600 dark:text-gray-400"
                          >Opcional</span
                        >
                      </label>
                    </div>
                  </div>

                  <!-- Actions -->
                  <div class="flex items-center justify-end gap-2 pt-1">
                    <button
                      type="button"
                      (click)="cerrarFormItem()"
                      class="px-2.5 py-1 text-xs rounded-md font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      (click)="guardarItem()"
                      [disabled]="
                        savingItem ||
                        itemForm.invalid ||
                        !itemForm.get('idProduct')?.value
                      "
                      class="px-2.5 py-1 text-xs rounded-md font-medium bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {{
                        savingItem
                          ? 'Guardando...'
                          : editingItemId !== null
                            ? 'Actualizar'
                            : 'Añadir'
                      }}
                    </button>
                  </div>
                </div>
              </div>
            }

            <!-- Items list -->
            @if (loadingItems) {
              <div
                class="flex items-center gap-2 text-gray-500 dark:text-gray-400 py-2"
              >
                <div
                  class="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-brand-500"
                ></div>
                <span class="text-xs">Cargando items...</span>
              </div>
            } @else if (bundleItems.length === 0 && !showItemForm) {
              <div
                class="flex items-center gap-2 text-gray-400 dark:text-gray-500 py-2"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  ></path>
                </svg>
                <span class="text-xs">No hay items en este paquete</span>
              </div>
            } @else {
              <div class="space-y-1.5">
                @for (item of bundleItems; track item.id) {
                  <div
                    class="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-gray-100 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/30"
                  >
                    <div class="flex items-center gap-3 min-w-0">
                      <div class="min-w-0 flex-1">
                        <div class="flex items-center gap-2">
                          <span
                            class="text-xs font-semibold text-gray-800 dark:text-white truncate"
                          >
                            {{ getProductName(item.idProduct) }}
                          </span>
                          @if (getProductSku(item.idProduct)) {
                            <span
                              class="text-[10px] font-mono text-gray-400 dark:text-gray-500 shrink-0"
                            >
                              {{ getProductSku(item.idProduct) }}
                            </span>
                          }
                        </div>
                        <div class="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span
                            class="text-[10px] text-gray-500 dark:text-gray-400"
                            >Cant: {{ item.quantity }}</span
                          >
                          @if (getProductPrice(item.idProduct) !== undefined) {
                            <span
                              class="text-[10px] text-brand-600 dark:text-brand-400 font-medium"
                            >
                              {{
                                formatPrecio(
                                  getProductPrice(item.idProduct),
                                  currentBundle?.currencyId
                                )
                              }}
                            </span>
                          }
                          @if (item.isOptional) {
                            <span
                              class="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300"
                              >Opcional</span
                            >
                          }
                        </div>
                      </div>
                    </div>
                    <div class="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        (click)="abrirFormEditarItem(item)"
                        class="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        title="Editar"
                      >
                        <svg
                          class="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          ></path>
                        </svg>
                      </button>
                      <button
                        type="button"
                        (click)="eliminarItem(item)"
                        class="rounded p-1 hover:bg-error-50 hover:text-error-600 text-gray-400"
                        title="Eliminar"
                      >
                        <svg
                          class="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          ></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        }

        <!-- Action buttons -->
        <div
          class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700"
        >
          <!-- Hint del motivo del disabled -->
          @if (
            (form.invalid || (!isEditing && bundleItems.length === 0)) &&
            !saving
          ) {
            <p class="text-xs text-error-500 mr-auto flex items-center gap-1.5">
              <svg
                class="w-4 h-4 shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              {{ disabledReason }}
            </p>
          }

          <app-button
            variant="outline"
            type="button"
            (btnClick)="cancelar()"
            >Cancelar</app-button
          >
          <app-button
            variant="primary"
            type="button"
            (btnClick)="guardar()"
            [disabled]="
              saving || form.invalid || (!isEditing && bundleItems.length === 0)
            "
          >
            {{
              saving
                ? 'Guardando...'
                : isEditing
                  ? 'Actualizar Paquete'
                  : 'Crear Paquete'
            }}
          </app-button>
        </div>
      </form>
    }
  </div>
</ng-template>
